import React, { useState } from "react";
import { KeyRound, LogOut } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { changePassword } from "../../services/auth.service";
import { PRODUCT_NAME } from "../../config/brand";

/**
 * First sign-in with a temporary password (parity audit 2, 4.2): the account
 * was made by an import or by the office, and its password has been in a
 * sheet or an e-mail. Nothing else opens until a new one is chosen — the
 * server refuses every other call (403 PASSWORD_CHANGE_REQUIRED), and this
 * screen stands in for every page meanwhile.
 */
export default function ForcePasswordChange() {
  const { user, updateUser, logout } = useAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const tooShort = form.next.length > 0 && form.next.length < 8;
  const mismatch = form.confirm.length > 0 && form.next !== form.confirm;
  const ready = form.current && form.next.length >= 8 && form.next === form.confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError("");
    try {
      await changePassword({ currentPassword: form.current, newPassword: form.next });
      updateUser({ mustChangePassword: false });
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || "Couldn't change the password");
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, autoComplete) => (
    <div>
      <label htmlFor={`fpc-${key}`} className="block text-sm font-semibold text-slate-700 mb-1">{label}</label>
      <input
        id={`fpc-${key}`}
        type="password"
        autoComplete={autoComplete}
        value={form[key]}
        onChange={(e) => { setForm({ ...form, [key]: e.target.value }); setError(""); }}
        className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
        data-testid={`fpc-${key}`}
      />
    </div>
  );

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-slate-50 p-4" data-testid="force-password-change">
      <form onSubmit={submit} className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600">
            <KeyRound className="w-5 h-5" />
          </span>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Choose a new password</h1>
            <p className="text-xs text-slate-500">{user?.name ? `${user.name} · ` : ""}{PRODUCT_NAME}</p>
          </div>
        </div>
        <p className="text-sm text-slate-600">
          You signed in with a temporary password. Choose your own before you continue — nothing else opens until you do.
        </p>
        {field("current", "Temporary password", "current-password")}
        {field("next", "New password (at least 8 characters)", "new-password")}
        {tooShort && <p className="text-xs text-rose-600">At least 8 characters.</p>}
        {field("confirm", "New password again", "new-password")}
        {mismatch && <p className="text-xs text-rose-600">The two do not match.</p>}
        {error && (
          <div className="text-sm rounded-lg bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2" data-testid="fpc-error">{error}</div>
        )}
        <div className="flex items-center justify-between gap-2 pt-1">
          <button type="button" onClick={logout} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-rose-600">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
          <button
            type="submit"
            disabled={busy || !ready}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
            data-testid="fpc-save"
          >
            {busy ? "Saving…" : "Set password"}
          </button>
        </div>
      </form>
    </div>
  );
}
